/**
 * AdminOverview — cross-investor admin view of the Investment module.
 *
 * Shown when the logged-in profile has role='admin' and no linked
 * member_id. Uses the same design tokens (gradient hero, card stack,
 * pill buttons) as the investor Home so the visual language stays
 * consistent across the module.
 */
import React, { useEffect, useState } from 'react';
import { getSupabase } from '../lib/supabase.js';
import LogCostModal from '../components/LogCostModal.jsx';
import CostSuggestionsCard from '../components/CostSuggestionsCard.jsx';
import {
  cycleSteps,
  deriveVelocityDays,
  labelLoopSteps,
} from '../lib/useInvestmentData.js';
import './home.css';
import { LoopDetailsPanel, LoopHeroLogo, LoopFactsStrip, PoolHeroCards } from './Home.jsx';
import PendingDrafts from './PendingDrafts.jsx';
import { shapeSchedule } from '../lib/payoutSchedule.js';

const fmt$ = (n) => '$' + (Math.round(Number(n) || 0)).toLocaleString();
// Signed variant for figures that are meaningfully negative (pool balances):
// "−$25,000" instead of "$-25,000".
const fmt$s = (n) => (Number(n) < 0 ? '−' : '') + fmt$(Math.abs(Number(n) || 0));
const fmt$Big = (n) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000) return '$' + (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1) + 'K';
  return '$' + v.toLocaleString();
};

export default function AdminOverview({ profile, navigate, beginActAs }) {
  // T-042: clicking an investor opens their investor view (act-as) directly —
  // the old per-member drill-down is retired; admin controls live on the
  // investor view's Admin tab.
  const openInvestor = (memberId) => {
    if (typeof beginActAs === 'function' && memberId) beginActAs(memberId);
  };
  const [state, setState] = useState({ status: 'loading', rows: [], catalog: null, loops: [], pendingList: [], pendingCosts: [], pendingEdits: [], pendingSchedules: [], costMembers: [], error: null });
  const [logCostOpen, setLogCostOpen] = useState(false);
  const [detailsLoop, setDetailsLoop] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb) return;
      try {
        const [membersRes, enrollRes, eventsRes, settlesRes, loopsRes, subsRes, refsRes, stepsRes, kindsRes] = await Promise.all([
          sb.from('members').select('id, name, display_name, managed_by_member_id, active').eq('active', true),
          sb.from('module_enrollment').select('*').eq('module_name', 'investment'),
          sb.from('value_events').select('enrolled_member_id, kareem_share, value_generated').is('voided_at', null),
          sb.from('investment_settlements').select('id, enrolled_member_id, amount, settle_date, payment_method, status, memo'),
          sb.from('loops').select('*'),
          sb.from('loop_subscriptions').select('id, enrolled_member_id, loop_id, active').eq('active', true),
          sb.from('loop_step_refs').select('*').order('step_order'),
          sb.from('steps').select('*'),
          sb.from('inv_account_kinds').select('name, label, logo_url'),
        ]);
        // D-14: investor-submitted one-time costs awaiting review. Fetched
        // tolerantly — before the status column exists this errors and the
        // card simply hides.
        const pendingItemsRes = await sb.from('investment_cost_items')
          .select('*').eq('status', 'pending').order('created_at', { ascending: false })
          .then((r) => r, () => ({ data: [] }));
        // T-051: investor-requested changes to booked cost lines.
        const pendingEditsRes = await sb.from('investment_cost_items')
          .select('*').not('pending_edit', 'is', null).order('created_at', { ascending: false })
          .then((r) => r, () => ({ data: [] }));
        // D-15: investor-created schedules awaiting the one-time approval.
        const pendingSchedRes = await sb.from('investment_cost_schedules')
          .select('*').eq('status', 'pending_approval').order('created_at', { ascending: false })
          .then((r) => r, () => ({ data: [] }));
        // Tk-C pool positions (T-010): admin call returns every household.
        // Fetched separately + tolerantly so the overview still renders if
        // the RPC is missing (pool card just hides its numbers as $0).
        const poolRes = await sb.rpc('v2_pool_position').then((r) => r, () => ({ data: null }));

        const members = membersRes.data || [];
        const enrollments = enrollRes.data || [];
        const events = eventsRes.data || [];
        const settles = settlesRes.data || [];
        const loops = loopsRes.data || [];
        const subs = subsRes.data || [];

        const poolBy = {};
        (poolRes.data || []).forEach((p) => { poolBy[p.household] = p; });

        // Contract summaries (Release 5 / T-022): the authoritative money
        // math, one call per household (admin may pass any member). Tolerant —
        // a missing/failed RPC leaves the legacy split-share math below in
        // charge for that row, so the overview always renders.
        const summaryBy = {};
        const scheduleBy = {}; // when each credit is due (each month's balance by the end of the next)
        await Promise.all((enrollRes.data || []).map(async (e) => {
          try {
            const { data: s, error: sErr } = await sb.rpc('v2_household_summary', { p_member_id: e.member_id });
            if (!sErr && s && typeof s === 'object' && s.holder) summaryBy[e.member_id] = s;
          } catch { /* row falls back to legacy math */ }
          try {
            const { data: d, error: dErr } = await sb.rpc('v2_settlement_schedule', { p_member_id: e.member_id });
            if (!dErr && d && typeof d === 'object' && d.holder) scheduleBy[e.member_id] = shapeSchedule(d);
          } catch { /* the row just shows no due date */ }
        }));

        const kareemShareBy = {}, valueBy = {}, settledBy = {}, pendingBy = {}, subCountBy = {};
        events.forEach((e) => {
          kareemShareBy[e.enrolled_member_id] = (kareemShareBy[e.enrolled_member_id] || 0) + Number(e.kareem_share || 0);
          valueBy[e.enrolled_member_id] = (valueBy[e.enrolled_member_id] || 0) + Number(e.value_generated || 0);
        });
        // Only confirmed settlements reduce outstanding; pending awaits Kareem's
        // review. T-027 (fixed 2026-10-23): a REJECTED row is neither — it used
        // to be bucketed and listed as pending, so Reject looked like it did
        // nothing and every click appended another "Rejected by Kareem" note.
        settles.forEach((s) => {
          const st = s.status || 'pending';
          const bucket = st === 'confirmed' ? settledBy : st === 'pending' ? pendingBy : null;
          if (bucket) bucket[s.enrolled_member_id] = (bucket[s.enrolled_member_id] || 0) + Number(s.amount || 0);
        });
        const pendingRows = settles.filter((s) => (s.status || 'pending') === 'pending');
        subs.forEach((s) => { subCountBy[s.enrolled_member_id] = (subCountBy[s.enrolled_member_id] || 0) + 1; });

        const memberMap = Object.fromEntries(members.map((m) => [m.id, m]));
        const rows = enrollments.map((e) => {
          const m = memberMap[e.member_id];
          const cfg = e.config || {};
          const hurdle = Number(cfg.kareem_initial_capital || 0);
          const recoveredGross = Number(cfg.kareem_recovered || 0);
          const split = Number(cfg.split_after_hurdle ?? 0.5);
          // D-15 Session C: contract-only. A household whose summary could
          // not be read degrades to a dash on its money figures (T-034's
          // per-row rule) — never a custody-blind fallback number.
          const S = summaryBy[e.member_id];
          const recovered = S ? Number(S.hurdleProgress || 0) : 0;
          const name = m ? (m.display_name || m.name) : e.member_id;
          return {
            id: e.member_id,
            name,
            initials: (name || '?').trim().charAt(0).toUpperCase(),
            enabled: e.enabled,
            hurdle,
            recovered: Math.round(recovered),
            hurdlePct: hurdle > 0 ? Math.min(1, recovered / hurdle) : 1,
            valueYTD: valueBy[e.member_id] || 0,
            pool: Number(poolBy[e.member_id]?.available ?? 0),
            poolPending: Number(poolBy[e.member_id]?.pending_in ?? 0),
            outstanding: S ? Math.round(Number(S.outstanding || 0)) : null,
            credit: Math.round(S ? Number(S.credit || 0) : 0),
            summaryMissing: !S,
            schedule: scheduleBy[e.member_id] || null,
            pending: pendingBy[e.member_id] || 0,
            subscribedLoops: subCountBy[e.member_id] || 0,
            cadence: cfg.settlement_cadence || 'monthly',
            color: memberColor(e.member_id),
          };
        });
        rows.sort((a, b) => (Number(b.enabled) - Number(a.enabled)) || (b.outstanding ?? -1) - (a.outstanding ?? -1) || a.name.localeCompare(b.name));

        const catalog = {
          totalLoops: loops.length,
          activeLoops: loops.filter((l) => l.active).length,
        };

        // Shape every loop for the side panel — same shape useInvestmentData
        // produces for Home, minus the per-investor allocations and events
        // (those are aggregated in the investor cards above).
        const refs = refsRes.data || [];
        const steps = stepsRes.data || [];
        const stepsById = Object.fromEntries(steps.map((s) => [s.id, s]));
        const kindMeta = Object.fromEntries((kindsRes.data || []).map((k) => [k.name, k]));
        const kindLabel = (slug) => (slug && (kindMeta[slug]?.label || slug.replace(/_/g, ' '))) || '';
        const kindLogo = (slug) => (slug && kindMeta[slug]?.logo_url) || null;
        const subsByLoop = {};
        subs.forEach((s) => { subsByLoop[s.loop_id] = (subsByLoop[s.loop_id] || 0) + 1; });
        const loopShapes = loops.map((l) => {
          const myRefs = refs.filter((r) => r.loop_id === l.id).sort((a, b) => a.step_order - b.step_order);
          const shapedSteps = myRefs.map((r) => {
            const step = stepsById[r.step_id] || {};
            return {
              id: step.id,
              idx: r.step_order,
              source: kindLabel(step.source_kind),
              sourceKind: step.source_kind || '',
              sourceLogo: kindLogo(step.source_kind),
              dest: kindLabel(step.dest_kind),
              destKind: step.dest_kind || '',
              destLogo: kindLogo(step.dest_kind),
              rate: Number(step.value_rate || 0),
              amount: step.amount == null ? null : Number(step.amount),
              stepDurationDays: Number(step.step_duration_days || 0),
              stepDurationIsBusiness: !!step.step_duration_is_business,
              waitDurationDays: Number(step.wait_duration_days || 0),
              waitDurationIsBusiness: !!step.wait_duration_is_business,
              movesYTD: 0,
              valueYTD: 0,
              executed: {},
            };
          });
          return {
            id: l.id,
            name: l.name,
            description: l.description || '',
            active: l.active,
            heroLogoUrl:    l.hero_logo_url || null,
            maxThroughput:  l.max_throughput == null ? null : Number(l.max_throughput),
            // These two were silently absent from the shape — opening the
            // LoopDetailsPanel from the Admin overview was reading them as
            // undefined and the admin edit form fell back to defaults
            // (per_cycle / 0). Saving then overwrote any real values that
            // had been set from CatalogAdmin or Home. Must mirror the
            // CatalogAdmin shape exactly so the panel sees the same loop
            // regardless of where it was opened from.
            maxThroughputUnit:     l.max_throughput_unit || 'per_cycle',
            monthlyOperatingCost:  Number(l.monthly_operating_cost || 0),
            // Auto-derived from steps (same pattern as blendedRate).
            velocityDays:   deriveVelocityDays(shapedSteps),
            rateOfReturn:   l.rate_of_return == null ? null : Number(l.rate_of_return),
            devicesNeeded:  l.devices_needed == null ? null : Number(l.devices_needed),
            paymentMethods: Array.isArray(l.payment_methods) ? l.payment_methods : [],
            steps: labelLoopSteps(shapedSteps),
            cyclesCompleted: 0,
            cyclesInProgress: 0,
            valueYTD: 0,
            blendedRate: cycleSteps(shapedSteps).reduce((s, st) => s + Number(st.rate || 0), 0),
            allocByMember: {},
            subscriberCount: subsByLoop[l.id] || 0,
          };
        });

        // Decorate pending rows with the investor name for the banner list.
        const pendingList = pendingRows.map((r) => ({
          ...r,
          memberName: (memberMap[r.enrolled_member_id]?.display_name || memberMap[r.enrolled_member_id]?.name || r.enrolled_member_id),
        }));

        // D-14: pending one-time costs, named for the review card.
        const pendingCosts = (pendingItemsRes.data || []).map((i) => ({
          ...i,
          memberName: (memberMap[i.member_id]?.display_name || memberMap[i.member_id]?.name || i.member_id),
        }));
        const pendingSchedules = (pendingSchedRes.data || []).map((s) => ({
          ...s,
          memberName: (memberMap[s.member_id]?.display_name || memberMap[s.member_id]?.name || s.member_id),
        }));
        // T-051: requested changes to booked cost lines (the line keeps its
        // figure until confirmed).
        const pendingEdits = (pendingEditsRes.data || []).map((i) => ({
          ...i,
          memberName: (memberMap[i.member_id]?.display_name || memberMap[i.member_id]?.name || i.member_id),
        }));
        // D-15: the admin Log-a-cost form books per individual member — but
        // only members of INVESTMENT households (enrollment holders + their
        // managed members). Anyone else (gold-module members) has no
        // household summary, so a cost booked to them would count nowhere.
        // Heads render plain; sub-members render "Head · Member".
        const costMembers = enrollments.flatMap((e) => {
          const head = memberMap[e.member_id];
          if (!head) return [];
          const headName = head.display_name || head.name;
          const subs = members.filter((m) => m.managed_by_member_id === e.member_id);
          return [
            { id: head.id, name: headName, household: null, sortKey: headName },
            ...subs.map((m) => ({
              id: m.id,
              name: m.display_name || m.name,
              household: headName,
              sortKey: headName + ' · ' + (m.display_name || m.name),
            })),
          ];
        }).sort((a, b) => a.sortKey.localeCompare(b.sortKey));

        if (!cancelled) setState({ status: 'ready', rows, catalog, loops: loopShapes, pendingList, pendingCosts, pendingEdits, pendingSchedules, costMembers, error: null });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', rows: [], catalog: null, loops: [], pendingList: [], pendingCosts: [], pendingEdits: [], pendingSchedules: [], costMembers: [], error: err });
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Admin: flip a pending settlement to confirmed. Triggers a reload so
  // the banner shrinks and the outstanding totals on investor cards update.
  // D-14: review an investor-submitted one-time cost. Confirm books it into
  // recognized costs (their claim drops); reject is terminal (T-027) and the
  // note lands on the row, visible to the investor. Single prompt only —
  // multi-prompt chains never complete (T-029).
  const reviewCostItem = async (item, action) => {
    const sb = getSupabase();
    if (!sb) return;
    let note = null;
    if (action === 'reject') {
      note = prompt(`Reject ${item.memberName}'s $${Number(item.amount || 0).toLocaleString()} cost ("${item.note}")? Optional note to the investor:`, '');
      if (note == null) return; // cancelled
    }
    const { error } = await sb.rpc('v2_review_cost_item', { p_item_id: item.id, p_action: action, p_note: note || null });
    if (error) { alert('Review failed: ' + (error.message || error)); return; }
    setReloadKey((k) => k + 1);
  };

  // D-15: one-time approval of an investor-created schedule. Approving turns
  // it active — its occurrences then fire as red confirm cards and each
  // month's charge STILL books only when confirmed. Reject retires it.
  // T-051: an investor's requested change to a booked cost line.
  const reviewCostEdit = async (item, action) => {
    const sb = getSupabase();
    if (!sb) return;
    let note = null;
    if (action === 'reject') {
      note = prompt(`Decline the change to $${Number(item.pending_edit?.amount).toLocaleString()} on "${item.note}"? The line stays at $${Number(item.amount).toLocaleString()}. Optional note to the investor:`, '');
      if (note == null) return;
    }
    const { error } = await sb.rpc('v2_review_cost_edit', { p_item_id: item.id, p_action: action, p_note: note || null });
    if (error) { alert('Review failed: ' + (error.message || error)); return; }
    setReloadKey((k) => k + 1);
  };

  const reviewSchedule = async (s, action) => {
    const sb = getSupabase();
    if (!sb) return;
    let note = null;
    if (action === 'reject') {
      note = prompt(`Reject ${s.memberName}'s recurring "${s.name}" ($${Number(s.amount || 0).toLocaleString()}/mo)? Optional note:`, '');
      if (note == null) return;
    }
    const { error } = await sb.rpc('v2_review_cost_schedule', { p_schedule_id: s.id, p_action: action, p_note: note || null });
    if (error) { alert('Review failed: ' + (error.message || error)); return; }
    setReloadKey((k) => k + 1);
  };

  const confirmSettlement = async (settlementId) => {
    const sb = getSupabase();
    if (!sb) return;
    const { error } = await sb.from('investment_settlements').update({ status: 'confirmed' }).eq('id', settlementId);
    if (error) { alert('Confirm failed: ' + (error.message || error)); return; }
    setReloadKey((k) => k + 1);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed'));
  };

  // T-025: the prompt used to offer Confirm ONLY, which once forced a wrong
  // accept (the Kholy 7/19 double-count). Reject marks the row rejected —
  // excluded from cash, pending, and outstanding everywhere — with the
  // reason kept on the memo, visible to the investor on their history.
  const rejectSettlement = async (row) => {
    const sb = getSupabase();
    if (!sb) return;
    if ((row.status || 'pending') !== 'pending') { alert('That payment is already ' + row.status + ' — nothing to reject.'); return; }
    const reason = prompt(`Reject ${row.memberName}'s ${fmt$(Number(row.amount || 0))} payment? Optional note to the investor:`, '');
    if (reason == null) return; // cancelled
    const memo = (row.memo ? row.memo + ' | ' : '') +
      'Rejected by Kareem ' + new Date().toISOString().slice(0, 10) + (reason.trim() ? ': ' + reason.trim() : '');
    const { error } = await sb.from('investment_settlements').update({ status: 'rejected', memo }).eq('id', row.id);
    if (error) { alert('Reject failed: ' + (error.message || error)); return; }
    setReloadKey((k) => k + 1);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed'));
  };

  // T-025: adjust a pending payment before confirming (amount / date /
  // method), trail kept on the memo. Row stays pending until Confirm.
  const editPendingSettlement = async (row) => {
    const sb = getSupabase();
    if (!sb) return;
    const amtStr = prompt('Amount ($):', String(Number(row.amount || 0)));
    if (amtStr == null) return;
    const amt = Number(amtStr);
    if (!Number.isFinite(amt) || amt <= 0) { alert('Enter a positive amount.'); return; }
    const date = prompt('Settle date (YYYY-MM-DD):', row.settle_date || new Date().toISOString().slice(0, 10));
    if (date == null) return;
    const methodName = prompt('Payment method:', row.payment_method || 'Other');
    if (methodName == null) return;
    const changes = [];
    if (amt !== Number(row.amount || 0)) changes.push(`amount ${fmt$(Number(row.amount || 0))}→${fmt$(amt)}`);
    if (date !== row.settle_date) changes.push(`date ${row.settle_date}→${date}`);
    if ((methodName || '') !== (row.payment_method || '')) changes.push(`method ${row.payment_method || '—'}→${methodName}`);
    const memo = changes.length
      ? (row.memo ? row.memo + ' | ' : '') + 'Edited by Kareem ' + new Date().toISOString().slice(0, 10) + ' (' + changes.join(', ') + ')'
      : row.memo;
    const { error } = await sb.from('investment_settlements')
      .update({ amount: amt, settle_date: date, payment_method: methodName.trim() || null, memo })
      .eq('id', row.id);
    if (error) { alert('Edit failed: ' + (error.message || error)); return; }
    setReloadKey((k) => k + 1);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed'));
  };

  const openCatalogAdmin = () => {
    if (typeof navigate === 'function') navigate('catalog-admin', {});
  };
  const openEnrollments = () => {
    if (typeof window === 'undefined') return;
    if (typeof window.switchTab === 'function') window.switchTab('settings');
    if (typeof window.setSettingsSubTab === 'function') window.setSettingsSubTab('general');
  };

  if (state.status === 'loading') {
    return <div className="app density-comfortable"><main className="page"><div className="card" style={{ color: 'var(--text-2)' }}>Loading admin overview…</div></main></div>;
  }
  if (state.status === 'error') {
    return (
      <div className="app density-comfortable"><main className="page">
        <div className="card" style={{ borderColor: 'var(--rose-border)', background: 'var(--rose-bg)', color: 'var(--rose)' }}>
          <strong>Couldn't load admin overview.</strong>
          <p className="mono" style={{ fontSize: 12, marginTop: 8 }}>{String(state.error?.message || state.error)}</p>
        </div>
      </main></div>
    );
  }

  const totalOutstanding = state.rows.filter((r) => r.enabled).reduce((s, r) => s + (r.outstanding || 0), 0);
  const totalValueYTD = state.rows.filter((r) => r.enabled).reduce((s, r) => s + r.valueYTD, 0);
  const totalHurdle = state.rows.filter((r) => r.enabled).reduce((s, r) => s + r.hurdle, 0);
  const totalRecovered = state.rows.filter((r) => r.enabled).reduce((s, r) => s + r.recovered, 0);
  const enrolledCount = state.rows.filter((r) => r.enabled).length;
  const portfolioPct = totalHurdle > 0 ? Math.min(1, totalRecovered / totalHurdle) : 0;

  return (
    <div className="app density-comfortable">
      <main className="page">
        {/* Page header */}
        <div className="page-title-row">
          <div>
            <div className="page-eyebrow"><span className="dot"></span> Investment · Admin overview</div>
            <h1 className="page-title">Across all investors</h1>
            <p className="page-sub">
              Signed in as <strong>{profile?.email || 'admin'}</strong> · no linked member. This is the platform view.
              To act as an investor with your own ledger, sign in with a member-linked account.
            </p>
          </div>
          <div className="page-actions">
            <button type="button" className="btn-ghost" onClick={openEnrollments}>Manage enrollments</button>
            <a className="btn-ghost" href="Email Sources.html" style={{textDecoration:'none',display:'inline-block'}}>Connectivity</a>
            <button type="button" className="btn-ghost" onClick={() => navigate && navigate('activity', {})}>All activity</button>
            <button type="button" className="btn-ghost" onClick={() => navigate && navigate('agent-health', {})}>Agent health</button>
            <button type="button" className="btn-ghost" onClick={() => setLogCostOpen(true)}>＋ Log a cost</button>
            <button type="button" className="btn-primary" onClick={openCatalogAdmin}>Open catalog admin →</button>
          </div>
        </div>

        {/* Hero — portfolio-wide hurdle progress */}
        <section className="hero">
          {/* T-009 ownership split: Kareem's admin login has no linked
              member, so his real Home is THIS screen — the four-card pool
              row lives here (and only renders its investor pair variant on
              the member-linked Home). */}
          <PoolHeroCards member={null} adminView />
          <div className="hero-grid">
            <div className="hero-main">
              <div className="hero-eyebrow">
                <span className="dot"></span>
                Portfolio hurdle · across {enrolledCount} active investor{enrolledCount === 1 ? '' : 's'}
              </div>
              <div className="hero-amount">
                <span className="hero-currency">$</span>
                <span className="hero-num">{totalRecovered.toLocaleString()}</span>
                <span className="hero-of">/ ${totalHurdle.toLocaleString()}</span>
              </div>
              <div className="hero-sub">
                <strong>${(totalHurdle - totalRecovered).toLocaleString()}</strong> remaining across all hurdles before the
                50/50 split kicks in everywhere. Until then, value goes 100% to Kareem per-investor.
              </div>
              <div className="hurdle-bar">
                <div className="hurdle-bar-track">
                  <div className="hurdle-bar-fill" style={{ width: (portfolioPct * 100) + '%' }}>
                    <div className="hurdle-bar-shine"></div>
                  </div>
                </div>
                <div className="hurdle-bar-labels">
                  <span>0</span>
                  <span className="hurdle-bar-current" style={{ left: `calc(${portfolioPct * 100}% - 1px)` }}>
                    <span className="caret"></span>
                    {(portfolioPct * 100).toFixed(1).replace(/\.0$/, '')}%
                  </span>
                  <span>${totalHurdle.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="hero-stats">
              <div className="hero-stat hs-warn">
                <div className="hs-label">Outstanding to Kareem</div>
                <div className="hs-value">{fmt$(totalOutstanding)}</div>
                <div className="hs-sub">across active investors</div>
              </div>
              <div className="hero-stat hs-ok">
                <div className="hs-label">Value generated · all-time</div>
                <div className="hs-value">{fmt$(totalValueYTD)}</div>
                <div className="hs-sub">sum of value_events</div>
              </div>
              <div className="hero-stat hs-neutral">
                <div className="hs-label">Enrolled investors</div>
                <div className="hs-value">{enrolledCount}</div>
                <div className="hs-sub">{state.rows.length === enrolledCount ? 'no disabled' : `${state.rows.length - enrolledCount} disabled`}</div>
              </div>
              <div className="hero-stat hs-neutral">
                <div className="hs-label">Catalog</div>
                <div className="hs-value">{state.catalog?.activeLoops || 0} / {state.catalog?.totalLoops || 0}</div>
                <div className="hs-sub">active loops</div>
              </div>
            </div>
          </div>
        </section>

        {/* T-009 Build 1: every household's pending TK-C draws (and any other
            pending movements) — the admin side of decision 4. RLS hands the
            admin ALL pending rows; the same component investors see on Home
            renders them here with per-member labels. */}
        <PendingDrafts member={null} profile={profile} />

        {/* Pending settlements — awaits Kareem's confirmation before they
            pay down outstanding. Members see these flagged on their home
            settlements list; Kareem confirms (or deletes) here. */}
        {state.pendingList && state.pendingList.length > 0 && (
          <section className="card amd-pending-card">
            <header className="card-header">
              <div className="card-title-block">
                <div className="card-eyebrow" style={{ color: 'var(--amber)' }}>Awaiting your confirmation</div>
                <h2 className="card-title">{state.pendingList.length} pending settlement{state.pendingList.length === 1 ? '' : 's'} · {fmt$(state.pendingList.reduce((s, r) => s + Number(r.amount || 0), 0))} total</h2>
                <p className="card-desc">These haven't paid down outstanding yet. Confirm once you've actually received the funds.</p>
              </div>
            </header>
            <div className="amd-pending-list">
              {state.pendingList.map((r) => (
                <div key={r.id} className="amd-pending-row">
                  <div className="amd-pending-member">
                    <button type="button" className="amd-pending-name-btn" onClick={() => openInvestor(r.enrolled_member_id)}>
                      {r.memberName}
                    </button>
                    <div className="amd-pending-date">{r.settle_date} · {r.payment_method || 'Other'}</div>
                  </div>
                  <div className="amd-pending-amt mono">{fmt$(Number(r.amount || 0))}</div>
                  <div className="amd-pending-memo">{r.memo || <span style={{ color: 'var(--text-3)' }}>no memo</span>}</div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button type="button" className="btn-primary-sm amd-pending-confirm" onClick={() => confirmSettlement(r.id)}>Confirm →</button>
                    <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => editPendingSettlement(r)}>Edit</button>
                    <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '6px 10px', color: 'var(--rose)' }} onClick={() => rejectSettlement(r)}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* D-14: investor-submitted one-time costs — confirm books the cost
            (their claim drops by it), reject is terminal with the note kept
            on the row. Mirrors the pending-settlements card above. */}
        {state.pendingCosts && state.pendingCosts.length > 0 && (
          <section className="card amd-pending-card">
            <header className="card-header">
              <div className="card-title-block">
                <div className="card-eyebrow" style={{ color: 'var(--amber)' }}>Awaiting your confirmation</div>
                <h2 className="card-title">{state.pendingCosts.length} submitted cost{state.pendingCosts.length === 1 ? '' : 's'} · {fmt$(state.pendingCosts.reduce((s, r) => s + Number(r.amount || 0), 0))} total</h2>
                <p className="card-desc">One-time costs investors logged themselves. Confirming books the cost — what they owe you drops by it. Rejecting keeps the row as a record with your note.</p>
              </div>
            </header>
            <div className="amd-pending-list">
              {state.pendingCosts.map((r) => (
                <div key={r.id} className="amd-pending-row">
                  <div className="amd-pending-member">
                    <button type="button" className="amd-pending-name-btn" onClick={() => openInvestor(r.member_id)}>
                      {r.memberName}
                    </button>
                    <div className="amd-pending-date">{(r.relates_to_month || r.month || '').slice(0, 7)}{r.relates_to_month ? ' (adjustment)' : ''} · their money</div>
                  </div>
                  <div className="amd-pending-amt mono">{fmt$(Number(r.amount || 0))}</div>
                  <div className="amd-pending-memo">{r.note}</div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button type="button" className="btn-primary-sm amd-pending-confirm" onClick={() => reviewCostItem(r, 'confirm')}>Confirm cost →</button>
                    <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '6px 10px', color: 'var(--rose)' }} onClick={() => reviewCostItem(r, 'reject')}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* T-051: investors' requested changes to booked cost lines. The line
            keeps its booked figure until you confirm; declining discards. */}
        {state.pendingEdits && state.pendingEdits.length > 0 && (
          <section className="card amd-pending-card amd-cost-edits">
            <header className="card-header">
              <div className="card-title-block">
                <div className="card-eyebrow" style={{ color: 'var(--accent-2, #3730a3)' }}>Awaiting your confirmation</div>
                <h2 className="card-title">{state.pendingEdits.length} requested change{state.pendingEdits.length === 1 ? '' : 's'} to booked costs</h2>
                <p className="card-desc">An investor edited a line that is already in the books. It keeps its current figure until you confirm the change; declining leaves it as it is, with your note.</p>
              </div>
            </header>
            <div className="amd-pending-list">
              {state.pendingEdits.map((r) => (
                <div key={r.id} className="amd-pending-row">
                  <div className="amd-pending-member">
                    <button type="button" className="amd-pending-name-btn" onClick={() => openInvestor(r.member_id)}>{r.memberName}</button>
                    <div className="amd-pending-date">{(r.relates_to_month || r.month || '').slice(0, 7)} · {r.pending_edit?.requested_by ? 'by ' + r.pending_edit.requested_by : 'by investor'}</div>
                  </div>
                  <div className="amd-pending-amt mono">
                    <span style={{ color: 'var(--text-3)', textDecoration: 'line-through' }}>{fmt$(Number(r.amount || 0))}</span> → {fmt$(Number(r.pending_edit?.amount || 0))}
                  </div>
                  <div className="amd-pending-memo">
                    {r.pending_edit?.note && r.pending_edit.note !== r.note ? <>“{r.note}” → “{r.pending_edit.note}”</> : r.note}
                    {r.pending_edit?.occurred_on && r.pending_edit.occurred_on !== r.occurred_on ? <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>date: {r.occurred_on || 'not set'} → {r.pending_edit.occurred_on}</div> : null}
                    {r.pending_edit?.reason ? <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>why: {r.pending_edit.reason}</div> : null}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button type="button" className="btn-primary-sm amd-pending-confirm" onClick={() => reviewCostEdit(r, 'confirm')}>Confirm change →</button>
                    <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '6px 10px', color: 'var(--rose)' }} onClick={() => reviewCostEdit(r, 'reject')}>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* D-15: kareem-paid expected costs — the payer attests, so these are
            the admin's red confirm cards. */}
        <CostSuggestionsCard variant="admin" />

        {/* D-15: investor-created schedules awaiting the one-time approval. */}
        {state.pendingSchedules && state.pendingSchedules.length > 0 && (
          <section className="card amd-pending-card">
            <header className="card-header">
              <div className="card-title-block">
                <div className="card-eyebrow" style={{ color: 'var(--amber)' }}>Awaiting your approval</div>
                <h2 className="card-title">{state.pendingSchedules.length} recurring cost{state.pendingSchedules.length === 1 ? '' : 's'} requested</h2>
                <p className="card-desc">Approving starts the monthly confirm card for them — each month's charge still books only when confirmed. Their money, their bill relief.</p>
              </div>
            </header>
            <div className="amd-pending-list">
              {state.pendingSchedules.map((s) => (
                <div key={s.id} className="amd-pending-row">
                  <div className="amd-pending-member">
                    <button type="button" className="amd-pending-name-btn" onClick={() => openInvestor(s.member_id)}>
                      {s.memberName}
                    </button>
                    <div className="amd-pending-date">monthly · bill day {s.bill_day}</div>
                  </div>
                  <div className="amd-pending-amt mono">{fmt$(Number(s.amount || 0))}/mo</div>
                  <div className="amd-pending-memo">{s.name}{s.note ? ` — ${s.note}` : ''}</div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button type="button" className="btn-primary-sm amd-pending-confirm" onClick={() => reviewSchedule(s, 'approve')}>Approve →</button>
                    <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '6px 10px', color: 'var(--rose)' }} onClick={() => reviewSchedule(s, 'reject')}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Investor cards grid */}
        <section className="card">
          <header className="card-header">
            <div className="card-title-block">
              <div className="card-eyebrow">Enrolled members</div>
              <h2 className="card-title">Investor ledger · {state.rows.length} total</h2>
              <p className="card-desc">One ledger per enrolled member. Click "Manage enrollments" to add or remove.</p>
            </div>
          </header>

          {state.rows.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>
              No one is enrolled in the investment module yet.
              {' '}
              <button type="button" onClick={openEnrollments} style={{ background: 'transparent', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                Open Settings → Executors
              </button>
              {' '}to toggle members on.
            </div>
          ) : (
            <div className="admin-investor-grid">
              {state.rows.map((r) => (
                <InvestorCard
                  key={r.id}
                  row={r}
                  onClick={() => openInvestor(r.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Loops catalog — click any loop to open the same side panel
            investors see on Home, with admin-edit for the headline facts. */}
        <section className="card">
          <header className="card-header">
            <div className="card-title-block">
              <div className="card-eyebrow">Loops catalog</div>
              <h2 className="card-title">All loops · {state.loops.length} total</h2>
              <p className="card-desc">Click any loop to view & edit its hero logo, headline facts, and payment method.</p>
            </div>
          </header>

          {state.loops.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>
              No loops in the catalog yet.{' '}
              <button type="button" onClick={openCatalogAdmin} style={{ background: 'transparent', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                Open catalog admin
              </button>
              {' '}to add one.
            </div>
          ) : (
            <div className="admin-loops-grid">
              {state.loops.map((lp) => (
                <AdminLoopCard key={lp.id} loop={lp} onClick={() => setDetailsLoop(lp)} />
              ))}
            </div>
          )}
        </section>

        <footer className="page-foot">
          Per-investor drilldowns (loop progress, value events ledger, settlements timeline) will appear once Block 4 (matching engine) and the remaining wired screens land.
        </footer>
      </main>

      {logCostOpen && (
        <LogCostModal
          mode="admin"
          members={state.costMembers}
          onClose={() => setLogCostOpen(false)}
          onSaved={() => { setLogCostOpen(false); setReloadKey((k) => k + 1); }}
        />
      )}
      {detailsLoop && (
        <LoopDetailsPanel
          loop={detailsLoop}
          members={[]}
          isAdmin={profile?.role === 'admin'}
          navigate={navigate}
          onLoopUpdated={() => { setDetailsLoop(null); setReloadKey((k) => k + 1); }}
          onClose={() => setDetailsLoop(null)}
        />
      )}

      <style>{`
        .admin-investor-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 14px;
          margin-top: 10px;
        }
        .admin-investor-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-left: 4px solid var(--accent);
          border-radius: var(--radius);
          padding: 16px 18px;
          box-shadow: var(--shadow-sm);
          display: flex;
          flex-direction: column;
          gap: 10px;
          position: relative;
        }
        .admin-investor-card.is-disabled { opacity: 0.55; border-left-color: var(--text-3); }
        .admin-investor-card.is-clickable {
          cursor: pointer;
          transition: transform .15s, box-shadow .15s, border-color .15s;
        }
        .admin-investor-card.is-clickable:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow);
          border-left-color: var(--accent-2);
        }
        .admin-investor-card.is-clickable:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
        .admin-investor-card.is-clickable.is-disabled { cursor: default; }
        .admin-investor-card.is-clickable.is-disabled:hover {
          transform: none; box-shadow: none; border-left-color: var(--text-3);
        }
        .aic-top { display: flex; align-items: center; gap: 10px; }
        .aic-avatar {
          width: 38px; height: 38px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-weight: 700; font-size: 14px;
          flex-shrink: 0;
        }
        .aic-name { font-weight: 700; font-size: 15px; color: var(--text-1); }
        .aic-meta { font-size: 11px; color: var(--text-3); margin-top: 2px; }
        .aic-status-pill {
          font-size: 10px; font-weight: 700; padding: 3px 8px;
          border-radius: 10px; letter-spacing: 0.04em; text-transform: uppercase;
          margin-left: auto;
        }
        .aic-status-pill.on { background: var(--green-bg); color: var(--green); border: 1px solid var(--green-border); }
        .aic-status-pill.off { background: var(--surface-2); color: var(--text-3); border: 1px solid var(--border); }
        .aic-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .aic-stat { background: var(--surface-2); padding: 8px 10px; border-radius: var(--radius-sm); }
        .aic-stat-lbl { font-size: 10px; color: var(--text-3); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
        .aic-stat-val { font-size: 16px; font-weight: 700; font-variant-numeric: tabular-nums; color: var(--text-1); margin-top: 2px; }
        .aic-stat-val.warn { color: var(--amber); }
        .aic-stat-val.ok { color: var(--green); }
        .aic-hurdle-bar {
          height: 6px; background: var(--surface-2); border-radius: 3px; overflow: hidden;
        }
        .aic-hurdle-fill {
          height: 100%; background: linear-gradient(90deg, var(--accent), var(--accent-2));
          border-radius: 3px;
        }
        .aic-foot {
          display: flex; align-items: center; gap: 8px;
          font-size: 11px; color: var(--text-3);
        }
        .admin-loops-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 14px;
          margin-top: 10px;
        }
        .alc-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          padding: 16px 18px;
          box-shadow: var(--shadow-sm);
          cursor: pointer;
          transition: transform .15s, box-shadow .15s, border-color .15s;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .alc-card:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow);
          border-color: var(--accent-soft);
        }
        .alc-card:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
        .alc-card.is-inactive { opacity: 0.55; }
        .alc-head { display: flex; align-items: center; gap: 12px; }
        .alc-title {
          flex: 1; min-width: 0;
        }
        .alc-eyebrow {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--text-3);
        }
        .alc-name {
          font-size: 15px; font-weight: 700; color: var(--text-1);
          margin-top: 2px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .alc-desc {
          margin: 6px 0 0;
          color: var(--text-2);
          font-size: 12px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .amd-pending-card {
          border-left: 4px solid var(--amber, #d97706);
          background: var(--amber-bg, #fef3c7);
        }
        .amd-pending-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 8px;
        }
        .amd-pending-row {
          display: grid;
          grid-template-columns: minmax(160px, 1.2fr) minmax(80px, 0.6fr) minmax(120px, 1fr) auto;
          gap: 12px;
          align-items: center;
          padding: 10px 14px;
          background: var(--surface);
          border: 1px solid var(--amber-border, #fcd34d);
          border-radius: var(--radius-sm, 8px);
        }
        .amd-pending-name-btn {
          background: transparent;
          border: none;
          padding: 0;
          font-weight: 700;
          color: var(--text-1);
          text-decoration: underline;
          cursor: pointer;
          font-size: 13.5px;
        }
        .amd-pending-name-btn:hover { color: var(--accent); }
        .amd-pending-date {
          font-size: 11px;
          color: var(--text-3);
          margin-top: 2px;
        }
        .amd-pending-amt { font-weight: 800; text-align: right; }
        .amd-pending-memo {
          font-size: 12.5px;
          color: var(--text-2);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .amd-pending-confirm { white-space: nowrap; }
        @media (max-width: 720px) {
          .amd-pending-row {
            grid-template-columns: 1fr auto;
          }
          .amd-pending-memo { grid-column: 1 / -1; }
        }
      `}</style>
    </div>
  );
}

// Compact loop card shown on the admin overview. Click anywhere on it to
// open the shared LoopDetailsPanel. The facts strip lives inside so the
// admin can scan headline numbers without opening every loop.
function AdminLoopCard({ loop, onClick }) {
  return (
    <div
      className={'alc-card' + (loop.active ? '' : ' is-inactive')}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <div className="alc-head">
        <LoopHeroLogo loop={loop} size={48} />
        <div className="alc-title">
          <div className="alc-eyebrow">Loop · {loop.subscriberCount} subscriber{loop.subscriberCount === 1 ? '' : 's'}</div>
          <div className="alc-name">{loop.name}</div>
        </div>
      </div>
      {loop.description && <p className="alc-desc">{loop.description}</p>}
      <LoopFactsStrip loop={loop} />
    </div>
  );
}

export function InvestorCard({ row, onClick }) {
  return (
    <div
      className={'admin-investor-card' + (row.enabled ? '' : ' is-disabled') + (onClick ? ' is-clickable' : '')}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="aic-top">
        <div className="aic-avatar" style={{ background: row.color }}>{row.initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="aic-name">{row.name}</div>
          <div className="aic-meta">{row.subscribedLoops} loop{row.subscribedLoops === 1 ? '' : 's'} subscribed · settles {row.cadence}</div>
        </div>
        <span className={'aic-status-pill ' + (row.enabled ? 'on' : 'off')}>{row.enabled ? 'Enrolled' : 'Disabled'}</span>
      </div>

      <div>
        <div className="aic-foot" style={{ marginBottom: 6 }}>
          <span>Hurdle</span>
          <span className="mono" style={{ marginLeft: 'auto' }}>{fmt$Big(row.recovered)} / {fmt$Big(row.hurdle)}{row.hurdle > 0 && ` · ${Math.round(row.hurdlePct * 100)}%`}</span>
        </div>
        <div className="aic-hurdle-bar">
          <div className="aic-hurdle-fill" style={{ width: (row.hurdlePct * 100) + '%' }}></div>
        </div>
      </div>

      <div className="aic-stats">
        <div className="aic-stat">
          <div className="aic-stat-lbl">{row.credit > 0 ? 'You owe them' : 'Outstanding'}</div>
          <div className={'aic-stat-val' + (row.credit > 0 ? ' ok' : row.outstanding > 0 ? ' warn' : '')}
            title={row.summaryMissing ? 'The summary service could not be reached for this household' : undefined}>
            {row.summaryMissing ? '— ⚠' : fmt$(row.credit > 0 ? row.credit : row.outstanding)}
          </div>
          {row.credit > 0 && row.schedule && (row.schedule.due >= 0.5 || row.schedule.building >= 0.5) && (
            <div style={{ fontSize: 10.5, fontWeight: 700, color: row.schedule.overdue >= 0.5 ? '#b45309' : 'var(--text-2)', marginTop: 2 }}>
              {row.schedule.due >= 0.5
                ? <>{fmt$(row.schedule.due)} by {row.schedule.dueByLabel}{row.schedule.overdue >= 0.5 ? <> · {fmt$(row.schedule.overdue)} overdue</> : null}</>
                : <>{fmt$(row.schedule.building)} by {row.schedule.buildingDueByLabel}</>}
            </div>
          )}
        </div>
        {/* T-010: pool balance replaced "Value · all-time" here — the pool
            position is the daily number; all-time value still lives in the
            member drill-down. Negative = the household owes into the pool. */}
        <div className="aic-stat">
          <div className="aic-stat-lbl">Pool balance</div>
          <div className={'aic-stat-val' + (row.pool > 0 ? ' ok' : row.pool < 0 ? ' warn' : '')}>{fmt$s(row.pool)}</div>
          {row.poolPending > 0 && (
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#b45309', marginTop: 2 }}>
              +{fmt$(row.poolPending)} pending
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Deterministic per-member color (same palette as Home).
const MEMBER_PALETTE = ['#4f46e5', '#0ea5e9', '#f59e0b', '#ec4899', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6', '#a855f7', '#06b6d4'];
function memberColor(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_PALETTE[h % MEMBER_PALETTE.length];
}
